import { computed, inject, signal } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions, LogLevel } from '@microsoft/signalr';
import { ENVIRONMENT } from '@privanot/shared-ui/providers';
import { PnToastService } from '@privanot/shared-ui/primeng-acl/toast';
import { TranslateService } from '@ngx-translate/core';
import { EntraAuthService } from '@client/auth/services/entra-auth.service';

type Unwrap<T> = T extends Promise<infer U> ? U : T;

/** Error code the Client Service returns for a call that needs Entra sign-in. */
const UNAUTHORIZED_ERROR = 'unauthorized';

/** Sent by the Client Service to a user's open windows when that user signs out elsewhere. */
const SESSION_ENDED_EVENT = 'sessionEnded';

/** Sent by the Client Service when it needs a newer token for this user, e.g. for a run in the background. */
const RENEW_TOKEN_EVENT = 'renewToken';

export interface WebsocketResponse<T> {
    errors: string[] | null;
    response: T | null;
    isSuccess: boolean;
}

export class WebsocketError extends Error {
    constructor(
        public readonly method: string,
        public readonly errors: string[],
    ) {
        super(`Websocket method "${method}" failed: ${errors.join(', ') || 'unknown error'}`);
        this.name = 'WebsocketError';
    }
}

export abstract class BaseWebsocketHubClient<
    TServerMethods extends { [K in keyof TServerMethods]: (...args: never[]) => unknown },
    TClientEvents extends { [K in keyof TClientEvents]: (...args: never[]) => void },
> {
    protected readonly env = inject(ENVIRONMENT);
    private readonly toastService = inject(PnToastService);
    private readonly translateService = inject(TranslateService);
    private readonly auth = inject(EntraAuthService);
    protected abstract readonly hubPath: string;

    private connection: HubConnection | null = null;
    private currentlyConnecting: Promise<void> | null = null;
    private messageQueue: { execute: () => void; cancel: (reason: Error) => void }[] = [];
    private refCount = 0;
    private stopping = false;
    private readonly reconnectedHandlers = new Set<() => void>();
    private readonly closedHandlers = new Set<() => void>();

    private readonly _state = signal<HubConnectionState>(HubConnectionState.Disconnected);
    public readonly state = this._state.asReadonly();
    public readonly isConnected = computed(() => this._state() === HubConnectionState.Connected);

    public async listen(): Promise<void> {
        this.refCount++;

        if (this.connection?.state === HubConnectionState.Connected) {
            return;
        }

        if (this.currentlyConnecting) {
            return this.currentlyConnecting;
        }

        this.connection = this.createConnection(`${this.env.BASE_URL}/${this.hubPath}`);

        this.connection.on(SESSION_ENDED_EVENT, () => void this.auth.handleUnauthorized());
        this.connection.on(RENEW_TOKEN_EVENT, () => void this.auth.renewNow());
        this.connection.onreconnecting(() => this._state.set(HubConnectionState.Reconnecting));
        this.connection.onreconnected(() => {
            this._state.set(HubConnectionState.Connected);
            this.flushMessageQueue();
            this.notify(this.reconnectedHandlers);
        });
        this.connection.onclose(() => {
            this._state.set(HubConnectionState.Disconnected);
            if (!this.stopping) {
                this.notify(this.closedHandlers);
            }
        });

        this.currentlyConnecting = (async () => {
            try {
                await this.connection!.start();
                this._state.set(HubConnectionState.Connected);
                this.flushMessageQueue();
            } finally {
                this.currentlyConnecting = null;
            }
        })();

        return this.currentlyConnecting;
    }

    protected createConnection(url: string): HubConnection {
        return new HubConnectionBuilder()
            .withUrl(url, this.connectionOptions())
            .withAutomaticReconnect()
            .configureLogging(this.env.isDevelopment ? LogLevel.Information : LogLevel.Warning)
            .build();
    }

    // SignalR calls the factory on every (re)connect, so a reconnect after the server closes an expired connection
    // picks up a renewed token. '' (Entra disabled) sends no token.
    protected connectionOptions(): IHttpConnectionOptions {
        return { accessTokenFactory: () => this.auth.getAccessToken() };
    }

    public async stopListening(): Promise<void> {
        if (this.refCount > 0) {
            this.refCount--;
        }
        if (this.refCount > 0) {
            return;
        }
        if (!this.connection) {
            return;
        }

        this.stopping = true;
        try {
            await this.connection.stop();
        } finally {
            this.stopping = false;
        }
        this.connection = null;
        this._state.set(HubConnectionState.Disconnected);
        this.rejectMessageQueue(new Error(`${this.constructor.name}: connection stopped before queued message could be sent.`));
    }

    public onReconnected(handler: () => void): () => void {
        this.reconnectedHandlers.add(handler);
        return () => this.reconnectedHandlers.delete(handler);
    }

    public onClosed(handler: () => void): () => void {
        this.closedHandlers.add(handler);
        return () => this.closedHandlers.delete(handler);
    }

    public async restart(): Promise<void> {
        const connection = this.requireConnection();
        if (connection.state !== HubConnectionState.Disconnected) {
            return;
        }

        await connection.start();
        this._state.set(HubConnectionState.Connected);
        this.flushMessageQueue();
        this.notify(this.reconnectedHandlers);
    }

    protected on<K extends keyof TClientEvents & string>(event: K, handler: TClientEvents[K]): () => void {
        const connection = this.requireConnection();
        connection.on(event, handler as unknown as (...args: unknown[]) => void);
        return () => connection.off(event, handler as unknown as (...args: unknown[]) => void);
    }

    protected invokeMethod<K extends keyof TServerMethods & string>(
        method: K,
        ...args: Parameters<TServerMethods[K]>
    ): Promise<Unwrap<ReturnType<TServerMethods[K]>>> {
        type T = Unwrap<ReturnType<TServerMethods[K]>>;
        return this.executeOrQueue(async () => {
            const result = await this.requireConnection().invoke<WebsocketResponse<T>>(method, ...args);

            if (!result.isSuccess) {
                const error = new WebsocketError(method, result.errors ?? []);
                if (error.errors.includes(UNAUTHORIZED_ERROR)) {
                    // Session ended (expired or signed out elsewhere): no toast, the app goes back to sign-in.
                    void this.auth.handleUnauthorized();
                } else {
                    this.toastWebsocketError(error);
                }
                throw error;
            }

            return result.response as T;
        });
    }

    private toastWebsocketError(error: WebsocketError): void {
        const detail = error.errors[0] ?? this.translateService.instant('errors.websocket.unknown');
        this.toastService.show({
            severity: 'error',
            summary: this.translateService.instant('errors.websocket.summary'),
            detail,
        });
    }

    protected sendMessage<K extends keyof TServerMethods & string>(method: K, ...args: Parameters<TServerMethods[K]>): Promise<void> {
        return this.executeOrQueue(() => this.requireConnection().send(method, ...args));
    }

    private executeOrQueue<R>(run: () => Promise<R>): Promise<R> {
        if (!this.connection) {
            throw new Error(`${this.constructor.name}: attempted to use connection before it was started.`);
        }

        if (this.connection.state === HubConnectionState.Connected) {
            return run();
        }

        return new Promise<R>((resolve, reject) => {
            this.messageQueue.push({
                execute: () => Promise.resolve().then(run).then(resolve, reject),
                cancel: reject,
            });
        });
    }

    // TODO: we might not want to flush some messages, maybe mark only fetch requests as queueable?
    // updates sitting in the queue could lead to bad things.
    private flushMessageQueue(): void {
        const queued = this.messageQueue;
        this.messageQueue = [];
        for (const item of queued) {
            item.execute();
        }
    }

    private rejectMessageQueue(reason: Error): void {
        const queued = this.messageQueue;
        this.messageQueue = [];
        for (const item of queued) {
            item.cancel(reason);
        }
    }

    private notify(handlers: Set<() => void>): void {
        for (const handler of [...handlers]) {
            handler();
        }
    }

    private requireConnection(): HubConnection {
        if (!this.connection) {
            throw new Error(`${this.constructor.name}: attempted to use connection before it was started.`);
        }

        return this.connection;
    }
}
